import { useState } from "react";
import { CheckCircle2, Mail, Phone } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/lib/SiteContext";

const inputClass =
  "w-full rounded-xl border border-choco-line bg-choco px-4 py-2.5 text-sm text-cream-text placeholder:text-cream-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus";

export default function Contact() {
  const { site } = useSite();
  const business = site?.business || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/public/contact", form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8" data-testid="contact-page">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
        <Reveal className="lg:col-span-2">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Contact</p>
          <h1 className="mt-3 font-serif text-3xl font-bold tracking-tight text-cream-text sm:text-4xl">
            Let's talk bakes
          </h1>
          <p className="mt-4 text-base leading-relaxed text-cream-muted">
            Questions about an order, an allergy, or a big event? Send us a message and we'll get back to you
            as soon as we can.
          </p>
          <div className="mt-8 space-y-4 text-sm">
            {business.email && (
              <p className="flex items-center gap-3 text-cream-muted">
                <Mail className="h-5 w-5 text-gold" aria-hidden="true" />
                <a href={`mailto:${business.email}`} className="hover:text-gold" data-testid="contact-email-link">{business.email}</a>
              </p>
            )}
            {business.phone && (
              <p className="flex items-center gap-3 text-cream-muted" data-testid="contact-phone">
                <Phone className="h-5 w-5 text-gold" aria-hidden="true" /> {business.phone}
              </p>
            )}
          </div>
          <p className="mt-8 rounded-2xl border border-gold/40 bg-choco-light p-4 text-sm leading-relaxed text-cream-muted" data-testid="contact-allergy-note">
            <strong className="text-cream-text">Allergies:</strong> if you have a food allergy, please contact
            us here before placing an order so we can advise you.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-3">
          {done ? (
            <div className="flex h-full flex-col items-center justify-center rounded-3xl bg-cream p-10 text-center text-cream-darktext" data-testid="contact-success-panel">
              <CheckCircle2 className="h-12 w-12 text-gold" aria-hidden="true" />
              <h2 className="mt-5 font-serif text-2xl font-bold">Message sent!</h2>
              <p className="mt-3 max-w-md text-sm text-cream-darkmuted">Thanks for getting in touch — we'll reply as soon as we can.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5 rounded-3xl border border-choco-line bg-choco-light p-6 sm:p-8" noValidate data-testid="contact-form">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-sm font-semibold text-cream-text">Full name *</label>
                  <input id="contact-name" required value={form.name} onChange={set("name")} className={inputClass} data-testid="contact-name-input" />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-sm font-semibold text-cream-text">Email *</label>
                  <input id="contact-email" type="email" required value={form.email} onChange={set("email")} className={inputClass} data-testid="contact-email-input" />
                </div>
              </div>
              <div>
                <label htmlFor="contact-phone" className="mb-1.5 block text-sm font-semibold text-cream-text">Phone</label>
                <input id="contact-phone" type="tel" value={form.phone} onChange={set("phone")} className={inputClass} data-testid="contact-phone-input" />
              </div>
              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-sm font-semibold text-cream-text">How can we help? *</label>
                <textarea id="contact-message" required rows={6} value={form.message} onChange={set("message")} className={inputClass} data-testid="contact-message-input" />
              </div>
              {error && <p className="text-sm font-semibold text-red-400" role="alert" data-testid="contact-error-message">{error}</p>}
              <button
                type="submit"
                disabled={busy}
                className="rounded-full bg-gold px-8 py-3 text-sm font-bold text-choco transition-colors duration-150 hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus focus-visible:ring-offset-2 focus-visible:ring-offset-choco active:scale-[0.98] disabled:opacity-60"
                data-testid="contact-submit-button"
              >
                {busy ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </div>
  );
}
